import { useEditorMaybe } from '@grapesjs/react';
import { Box, FormControl, FormHelperText, TextField, Tooltip } from '@mui/material';
import { RiFileCopyLine } from '@remixicon/react';
import { GLOBAL_DEFAULT_PRODUCT_DESCRIPTION_TEMPLATE } from '@shared/core/product/description';
import { useCallback } from 'react';
import Autocomplete from 'src/components/atoms/Autocomplete';
import { AgButton, AgCheckbox, AgModal, AgModalActions, AgModalContent } from 'src/components/ui';
import { useProductDescriptionTemplate } from 'src/modules/entities/organization';
import {
  type DocumentOptions,
  documentTypeGroups,
  documentTypeOptions,
  type GenerationOptions,
} from '../pages/DocumentEditorPage/documentEditorOptions';

type DocumentStorageSaveModalProps = {
  open: boolean;
  onClose: () => void;
  documentName: string;
  setDocumentName: (documentName: string) => void;
  documentType: DocumentOptions | null;
  setDocumentType: (documentType: DocumentOptions | null) => void;
  generationOptions: GenerationOptions | null;
  setGenerationOptions: (generationOptions: GenerationOptions) => void;
  showInBulkPrintMenu: boolean;
  setShowInBulkPrintMenu: (showInBulkPrintMenu: boolean) => void;
};

const DocumentTypeSelection = ({ value, setDocumentType, ...props }) => {
  return (
    <Autocomplete
      options={documentTypeOptions}
      value={value}
      onChange={(event, newValue) => {
        setDocumentType(newValue);
      }}
      getOptionLabel={(option) => option.label}
      groupBy={(option) => {
        return documentTypeGroups[option.entity];
      }}
      size="small"
      {...props}
    />
  );
};

export default function DocumentStorageSaveModal({
  open,
  onClose,
  documentName,
  documentType,
  setDocumentName,
  setDocumentType,
  generationOptions,
  setGenerationOptions,
  showInBulkPrintMenu,
  setShowInBulkPrintMenu,
}: DocumentStorageSaveModalProps) {
  const editor = useEditorMaybe();
  const productDescriptionTemplate = useProductDescriptionTemplate() || GLOBAL_DEFAULT_PRODUCT_DESCRIPTION_TEMPLATE;

  const handleSaveConfirm = useCallback(() => {
    editor.store({
      remote: true,
      documentName,
      documentType,
      generationOptions: generationOptions ?? {},
      showInBulkPrintMenu,
    });
    onClose();
  }, [editor, documentName, documentType, onClose, generationOptions, showInBulkPrintMenu]);

  return (
    <AgModal open={open} onClose={onClose} title="Save changes to document?" size="xs">
      <AgModalContent>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Document Name"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
          />
          <DocumentTypeSelection
            textFieldProps={{ fullWidth: true }}
            width="100%"
            size="large"
            label="Document Type"
            value={documentType}
            setDocumentType={setDocumentType}
          />
          <FormControl>
            <TextField
              label="Product Description Template"
              value={generationOptions?.productDescriptionTemplate || ''}
              multiline
              onChange={(e) => {
                const existingGenerationOptions = generationOptions || {};

                setGenerationOptions({ ...existingGenerationOptions, productDescriptionTemplate: e.target.value });
              }}
            />
            <FormHelperText>
              This template is used to generate the product description for the document.{' '}
              <Tooltip title="Copy org's product description to clipboard">
                <AgButton
                  variant="ghost"
                  size="icon-xs"
                  onPress={() => navigator.clipboard.writeText(productDescriptionTemplate)}
                >
                  <RiFileCopyLine size={20} />
                </AgButton>
              </Tooltip>
            </FormHelperText>
          </FormControl>
          <AgCheckbox
            label="Show in bulk printing menu"
            isSelected={!!showInBulkPrintMenu}
            onChange={setShowInBulkPrintMenu}
          />
        </Box>
      </AgModalContent>
      <AgModalActions>
        <AgButton variant="primary" onPress={handleSaveConfirm}>
          Save
        </AgButton>
      </AgModalActions>
    </AgModal>
  );
}
